<template>
  <ak-card-header class="flex flex--center">
    <div class="flex__item flex flex--center">
      <h4 class="color-grey">{{ contactsOwner }} {{ contactsBranding }}</h4>
      <i
        v-if="currentWidget === 'allContacts'"
        class="ml-tiny fa fa-solid fa-user-group"
      />
      <i
        v-if="currentWidget === 'myContacts'"
        class="ml-tiny fa fa-solid fa-user"
      />
      <ak-dropdown
        size="auto"
        aria-role="list"
        class="ml-tiny contacts-dropdown"
      >
        <template #trigger>
          <ak-button color="transparent" size="small" circle>
            <span class="caret"></span>
          </ak-button>
        </template>
        <template #default>
          <ak-dropdown-item
            class="flex flex--center pt-0 pb-tiny px-tiny"
            @click="selectWidget('allContacts')"
          >
            <i class="fa fa-solid fa-user-group mr-tiny min-width-20" />
            <span class="text-nowrap">All {{ contactsBranding }}</span>
          </ak-dropdown-item>
          <ak-dropdown-item
            class="flex flex--center py-0 px-tiny"
            @click="selectWidget('myContacts')"
          >
            <i class="fa fa-solid fa-user mr-tiny min-width-20" />
            <span class="text-nowrap">My {{ contactsBranding }}</span>
          </ak-dropdown-item>
        </template>
      </ak-dropdown>
    </div>
    <div>
      <ak-tooltip
        v-if="isAllowed('contacts')"
        :placement="'left'"
        :has-arrow="false"
      >
        <template #trigger>
          <router-link :to="viewAllContacts">
            <ak-button circle size="small" color="transparent">
              <i class="color-primary icon-open" />
            </ak-button>
          </router-link>
        </template>
        View All
      </ak-tooltip>
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <ak-button
            circle
            size="small"
            color="transparent"
            @click="changeWidget()"
          >
            <i class="color-grey-light icon-cog" />
          </ak-button>
        </template>
        Change Widget
      </ak-tooltip>
    </div>
  </ak-card-header>
  <ak-card-body :size="'flush'" class="flex--column overflow-y h-full">
    <ak-display-table v-if="isLoading" cosy>
      <ak-display-table-row
        v-for="number in n"
        :key="number"
        class="c-skeleton-loading"
      >
        <ak-display-table-cell class="width-40">
          <div
            class="circle circle--large bg-grey-lighter c-skeleton-loading__item"
          ></div>
        </ak-display-table-cell>
        <ak-display-table-cell>
          <ak-pack>
            <ak-pack-item>
              <div class="c-faux-text width-125" />
              <div>
                <p class="c-faux-text width-100 mb-none"></p>
              </div>
            </ak-pack-item>
          </ak-pack>
        </ak-display-table-cell>
        <ak-display-table-cell class="cell--1">
          <div class="c-faux-text width-50" />
          <div class="c-faux-text width-50" />
        </ak-display-table-cell>
      </ak-display-table-row>
    </ak-display-table>

    <ak-table
      v-model:data="contacts"
      hide-head
      hoverable
      background
      spacing="tiny"
      :empty-state="emptyState"
      :class="{ 'h-full flex': !contacts.length }"
    >
      <template #default="{ row }">
        <ak-table-column :width="100">
          <router-link
            :to="viewContact(row.id)"
            class="media media--flush repaint color-text"
            @click="row.clicked = true"
          >
            <div
              class="media__figure p-small"
              :class="{ 'pr-0 pt-tiny': row.clicked }"
            >
              <div
                :class="{ 'py-tiny': row.clicked }"
                class="score score--has-avatar pull-left"
              >
                <div
                  class="score score--cold"
                  :class="{
                    'score--tepid': row.customScore.useStaticScore
                      ? row.customScore.staticScore >=
                        accountObj().settings.leads.score.heat.tepid.min
                      : row.score + row.customScore.modifier >=
                        accountObj().settings.leads.score.heat.tepid.min,
                    'score--warm': row.customScore.useStaticScore
                      ? row.customScore.staticScore >=
                        accountObj().settings.leads.score.heat.warm.min
                      : row.score + row.customScore.modifier >=
                        accountObj().settings.leads.score.heat.warm.min,
                    'score--hot': row.customScore.useStaticScore
                      ? row.customScore.staticScore >=
                        accountObj().settings.leads.score.heat.hot.min
                      : row.score + row.customScore.modifier >=
                        accountObj().settings.leads.score.heat.hot.min,
                  }"
                >
                  <ak-tooltip
                    v-if="
                      row.customScore.useStaticScore
                        ? row.customScore.staticScore > 999
                        : row.score + row.customScore.modifier > 999
                    "
                    :placement="'right'"
                    :has-arrow="false"
                  >
                    <template #trigger>
                      <div class="score__figure">
                        <i class="icon-fireball inline-block" />
                      </div>
                    </template>
                    {{
                      row.customScore.useStaticScore
                        ? row.customScore.staticScore
                        : row.score + row.customScore.modifier
                    }}
                  </ak-tooltip>
                  <div
                    v-if="
                      row.customScore.useStaticScore
                        ? row.customScore.staticScore <= 999
                        : row.score + row.customScore.modifier <= 999
                    "
                    class="score__figure"
                  >
                    <span
                      v-if="
                        row.customScore.useStaticScore
                          ? row.customScore.staticScore <= 999
                          : row.score + row.customScore.modifier < 999
                      "
                    >
                      {{
                        row.customScore.useStaticScore
                          ? row.customScore.staticScore
                          : row.score + row.customScore.modifier
                      }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="row.clicked" class="avatar-wrapper pull-left">
                <ak-loading-spinner :size="'small'" />
              </div>
              <div v-if="!row.clicked" class="avatar-wrapper pull-left">
                <ak-avatar
                  v-if="row.displayData !== false && !row.suppressed"
                  :string="row.fullname"
                  :size="38"
                />
                <ak-avatar
                  v-if="row.displayData === false"
                  :string="'–'"
                  :size="38"
                />
                <ak-tooltip
                  v-if="row.suppressed && row.displayData !== false"
                  theme="light"
                  :placement="'right'"
                >
                  <template #trigger>
                    <div class="avatar avatar--initial-replacement bg-warning">
                      <i class="icon-warning icon--big mb-micro"></i>
                    </div>
                  </template>
                  <div class="flex flex--column max-width-300">
                    <strong class="mb-micro color-warning">
                      Globally Suppressed
                    </strong>
                    <p class="text-small mb-0">
                      This {{ contactBranding }} is Globally suppressed as they
                      have clicked on the unsubscribe link within an
                      {{ !isXEquals() ? 'Akero' : 'XEquals' }} email. They will
                      no longer receive any non-transactional emails that you
                      send via {{ !isXEquals() ? 'Akero' : 'XEquals' }}.
                    </p>
                  </div>
                </ak-tooltip>
              </div>
            </div>
          </router-link>
        </ak-table-column>
        <ak-table-column>
          <router-link :to="viewContact(row.id)" @click="row.clicked = true">
            <div class="flex flex--column">
              {{ row.fullname || 'No Name Given' }}
              <br />
              <span v-if="row.email[0].value" class="text-small">
                <strong v-if="row.suppressed" class="color-warning"
                  >Suppressed: </strong
                >{{ row.email[0].value }}
              </span>
              <span v-if="!row.email[0].value" class="text-small"> – </span>
            </div>
          </router-link>
        </ak-table-column>
        <ak-table-column
          field="row.createdOn"
          class="text-nowrap text-small px cell--1"
        >
          <router-link :to="viewContact(row.id)" @click="row.clicked = true">
            <strong>{{ formatDate(row.createdOn) }}</strong>
            <br />
            <em>{{ formatTime(row.createdOn) }}</em>
          </router-link>
        </ak-table-column>
      </template>
      <template #empty>
        <div></div>
      </template>
    </ak-table>
  </ak-card-body>
</template>

<script>
import { accountObj, agencyObj, userObj } from '@akerolabs/akero-vue/src/store'
import { defineComponent, ref, computed, onMounted } from 'vue'
import { entityBranding } from '@akerolabs/akero-vue/src/services/entityBranding.js'
import { isAllowed } from '@akerolabs/akero-vue/src/services/features.js'
import { isXEquals } from '@akerolabs/akero-vue/src/services/agency'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src'
import Avatar from '@akerolabs/akero-vue/src/components/avatar/Avatar.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import date from '@akerolabs/akero-vue/src/utils/date'
import LoadingSpinner from '@akerolabs/akero-vue/src/components/loading/LoadingSpinner.vue'
import Popover from '@akerolabs/akero-vue/src/components/popover/Popover.vue'
import Table from '@akerolabs/akero-vue/src/components/table/Table.vue'
import TableColumn from '@akerolabs/akero-vue/src/components/table/TableColumn.vue'

export default defineComponent({
  name: 'AkWidgetContacts',

  components: {
    [Avatar.name]: Avatar,
    [Button.name]: Button,
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    [LoadingSpinner.name]: LoadingSpinner,
    [Popover.name]: Popover,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  inheritAttrs: false,

  props: {
    /**
     * @type {Function}
     * @description change widget function
     */
    changeWidget: {
      default: () => ({}),
      type: Function,
    },
    /**
     * @type {}
     * @description current widget name
     */
    currentWidget: {
      default: '',
      type: String,
    },
    /**
     * @type {String}
     * @description who leads/contacts belong to
     */
    owner: {
      default: 'all',
      type: String,
    },
  },

  setup(props) {
    let n = 10
    const contactsOwner = ref('')
    const contacts = ref([])
    const isLoading = ref(true)
    /**
     * @param {*} widget
     * @description Changes widget to another
     */
    const selectWidget = (widget) => {
      props.changeWidget(widget)
    }
    /**
     * @description Route to leads table
     */
    const viewAllContacts = computed(() => {
      return { name: 'contact.manager.contact.manage' }
    })
    /**
     * @description User defined lead branding
     */
    const contactsBranding = computed(() => {
      return entityBranding('contacts.plural', 'Contacts')
    })

    /**
     * @description User defined lead branding
     */
    const leadsBranding = computed(() => {
      return entityBranding('leads.plural', 'Leads')
    })

    const viewContact = (contactId) => {
      return {
        name: 'contact.manager.contact.view.overview',
        params: { id: contactId },
      }
    }

    const fetchContacts = async () => {
      let contactsData = []
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load requests`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get('/contacts', contactsRequest.value)
        contactsData = data.data
      } catch (ex) {
        contactsData = []
        displayError(ex)
      }
      contacts.value = contactsData
      isLoading.value = false
    }

    const contactsRequest = computed(() => {
      let params = {
        account: accountObj()._id,
        agency: agencyObj()._id,
        deleted: JSON.stringify({ $ne: true }),
        test: JSON.stringify({ $ne: true }),
        hidden: JSON.stringify({ $ne: true }),
      }
      if (props.owner === 'my') {
        params['owner'] = userObj()._id
        return {
          params,
        }
      }
      return {
        params,
      }
    })

    /**
     *
     * @param {*} dateString
     * @description format createdOn into DD/MM/YYYY
     */
    const formatDate = (dateString) => {
      return date(dateString).format('DD/MM/YY')
    }

    /**
     *
     * @param {*} dateString
     * @description format createdOn into HH:MM
     */
    const formatTime = (dateString) => {
      return date(dateString).format('h:mm a')
    }

    /**
     *
     * @description Create empty state for table
     */
    const emptyState = {
      title: 'Enhance Profiles',
      icon: 'people',
      text:
        "Once you've captured a " +
        entityBranding('leads.singular', 'Lead') +
        " you'll be able to track all activity against a single profile",
    }

    onMounted(async () => {
      if (props.owner === 'all') {
        contactsOwner.value = 'All'
      }
      if (props.owner === 'my') {
        contactsOwner.value = 'My'
      }
      await fetchContacts()
    })

    return {
      accountObj,
      contacts,
      contactsBranding,
      contactsOwner,
      formatDate,
      formatTime,
      isAllowed,
      isLoading,
      isXEquals,
      leadsBranding,
      n,
      selectWidget,
      viewAllContacts,
      viewContact,
      emptyState,
    }
  },
})
</script>
